<template>
    <div class="Transfer Card Card--top">
        <div class="Card-header">
            <button class="Card-headerControl Button Transfer-close" @click="$emit('close', true)">&times;</button>
            <h2 class="Card-title">Transfer options for Ticket {{ticket.code}}</h2>
        </div>
        <div class="Card-body">
            <h3>Guest name: {{ticket.name}}</h3>
            <p>Source event: {{ ticket.event.name }}, {{ticket.event.date}} ({{ticket.event.currency_symbol}}{{ticket.event.price }}). {{ticket.quantity}} tickets.</p>
            <form @submit="transferTicket($event)">
                <input-field
                    :value="targetEventSearch"
                    :definition="{
                        fieldType: 'typeahead',
                        label: 'Event to transfer ticket to:',
                        url: 'events/search',
                        upcomingOnly: true
                    }"
                    @change="updateTypeaheadString($event)"
                    v-on:selected="updateTargetEvent($event)">
                </input-field>
                <p v-if="targetEvent">
                    Target event: {{targetEvent.id}}: {{targetEvent.name}}, {{targetEvent.date}} ({{targetEvent.currency_symbol}}{{targetEvent.price }})<br>
                    Tickets remaining: {{targetEvent.quantity - targetEvent.sold}}
                </p>

                <div
                    v-if="settlementPreview && settlementPreview.headline"
                    class="Transfer-settlementPreview"
                    data-cy="transfer-settlement-preview">
                    <p class="Transfer-settlementPreview-headline">{{ settlementPreview.headline }}</p>
                    <p v-if="settlementPreview.amountLine" class="Transfer-settlementPreview-amount">
                        Amount: {{ settlementPreview.amountLine }}
                    </p>
                </div>

                <p class="Transfer-note">
                    Note: customer communications for top-ups and refunds are handled manually in v1.
                </p>

                <div v-if="transferStatusMessage" class="Transfer-banner Transfer-banner--success" data-cy="transfer-status-success">
                    {{ transferStatusMessage }}
                </div>

                <div v-if="settlementReference" class="Transfer-settlementPanel" data-cy="transfer-settlement-panel">
                    <p class="Transfer-settlementState" data-cy="transfer-settlement-state-label">{{ settlementStateLabel }}</p>
                    <p v-if="settlementStateDetail" class="Transfer-settlementDetail">{{ settlementStateDetail }}</p>
                    <p v-if="formattedInvoiceExpiry" class="Transfer-invoiceExpiry" data-cy="transfer-invoice-expiry">
                        Invoice expires: {{ formattedInvoiceExpiry }}
                    </p>
                    <p v-if="settlementReference" class="Transfer-supportRef" data-cy="transfer-settlement-reference">
                        Settlement reference: {{ settlementReference }}
                    </p>

                    <div v-if="settlementInvoiceUrl" class="Transfer-invoiceActions">
                        <a
                            :href="settlementInvoiceUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-cy="transfer-invoice-link">Open invoice</a>
                        <button
                            type="button"
                            class="Button Button--secondary Transfer-copyInvoice"
                            data-cy="transfer-copy-invoice"
                            @click="copyInvoiceLink">Copy invoice link</button>
                        <span v-if="copyInvoiceMessage" class="Transfer-copyFeedback" data-cy="transfer-copy-feedback">{{ copyInvoiceMessage }}</span>
                    </div>

                    <button
                        v-if="showRefreshSettlement"
                        type="button"
                        class="Button Button--secondary Transfer-refreshStatus"
                        data-cy="transfer-refresh-status"
                        :disabled="refreshInFlight"
                        @click="refreshSettlementStatus">Refresh status</button>
                </div>

                <button
                    class="Button"
                    :disabled="isDisabled"
                    type="submit"
                    data-cy="transfer-submit">Transfer ticket</button>

                <p v-if="inlineValidationError" class="Error" data-cy="transfer-inline-error">{{inlineValidationError}}</p>
                <p v-if="asyncFormError" class="Error" data-cy="transfer-api-error">{{asyncFormError}}</p>
                <button
                    v-if="showResendTicket"
                    type="button"
                    class="Button Transfer-resendTicket"
                    data-cy="transfer-resend-ticket"
                    :disabled="isResendDisabled"
                    @click="resendTicketEmail">
                    {{ resendingTicket ? 'Resending…' : 'Resend ticket' }}
                </button>
            </form>
        </div>
    </div>
</template>

<script>
    import cookieModel from '../../models/cookie';
    import axios from "axios";
    import errorModel from "../../models/error";
    import toastModel from '../../models/toast';
    import moment from 'moment';
    import InputField from '../input-field/input-field.vue';
    import { getSettlementPreview, transferPriceDelta } from '../../utils/transfer-settlement-preview';
    import {
        buildTicketResendPayload,
        isResendTicketDisabled,
        isTicketResendQueued,
        TICKET_RESEND_PATH,
    } from '../../pages/Search/search-utils';
    import './_Transfer.scss';

    const SETTLEMENT_STATUS_PATH = '/einstein/ticket/transfer/settlement-status';

    export default {
        components: {
          InputField,
        },
        props: {
            ticket: Object,
        },
        computed: {
          inlineValidationError: function() {
              if (!this.targetEvent) {
                  return 'Please select an event';
              }
              if (this.targetEvent.quantity - this.targetEvent.sold < 1) {
                  return 'No tickets remaining for this event';
              }
              return '';
          },
          isDisabled: function() {
              return !!this.inlineValidationError;
          },
          settlementPreview: function() {
              if (!this.targetEvent || !this.ticket || !this.ticket.event) {
                  return null;
              }
              return getSettlementPreview({
                  sourceUnitPrice: this.ticket.event.price,
                  targetUnitPrice: this.targetEvent.price,
                  quantity: this.ticket.quantity,
                  currencySymbol: this.targetEvent.currency_symbol || this.ticket.event.currency_symbol,
              });
          },
          priceDelta: function() {
              if (!this.targetEvent || !this.ticket || !this.ticket.event) {
                  return null;
              }
              return transferPriceDelta(
                  this.ticket.event.price,
                  this.targetEvent.price,
                  this.ticket.quantity,
              );
          },
          formattedInvoiceExpiry: function() {
              if (!this.invoiceExpiresAt) {
                  return '';
              }
              const m = moment(this.invoiceExpiresAt);
              return m.isValid() ? m.format('D MMM YYYY, HH:mm') : this.invoiceExpiresAt;
          },
          settlementStateLabel: function() {
              const labels = {
                  awaiting_settlement: 'Awaiting settlement',
                  awaiting_customer: 'Awaiting customer payment',
                  settled: 'Settled',
                  failed: 'Settlement failed',
                  expired: 'Invoice expired',
              };
              return labels[this.settlementState] || this.settlementState || 'In progress';
          },
          settlementStateDetail: function() {
              if (this.settlementState === 'failed') {
                  return 'This transfer could not be completed. Please contact support with the ticket code and settlement reference so we can investigate.';
              }
              if (this.settlementState === 'expired') {
                  return 'The top-up invoice has expired (seven-day window). Please contact support to arrange a new invoice or another resolution.';
              }
              if (this.priceDelta != null && this.priceDelta < 0) {
                  if (this.settlementState === 'awaiting_settlement' || this.settlementState === 'awaiting_customer') {
                      return 'Processing refund';
                  }
              }
              return '';
          },
          showResendTicket: function() {
              if (!this.canResendTicket) {
                  return false;
              }
              return this.immediateTransferCompleted || this.settlementState === 'settled';
          },
          isResendDisabled: function() {
              const ticketCode = String(this.ticket.code);
              return isResendTicketDisabled(ticketCode, {
                  resentTicketCodes: this.ticketResent ? [ticketCode] : [],
                  resendingTicketCode: this.resendingTicket ? ticketCode : null,
              });
          },
          showRefreshSettlement: function() {
              if (!this.settlementReference) {
                  return false;
              }
              return ['failed', 'expired', 'settled'].indexOf(this.settlementState) === -1;
          },
        },
        methods: {
            applyAuthHeader: function() {
                this.axiosInstance.defaults.headers.common['Authorization'] = 'Bearer ' + cookieModel.getCookie('ticketlab_access');
            },
            requestUrl: function(path) {
                const p = path.startsWith('/') ? path : '/' + path;
                const base = this.baseUrl;
                if (!base || base === '/') {
                    return p;
                }
                return String(base).replace(/\/+$/, '') + p;
            },
            resetTransferOutcome: function() {
                this.asyncFormError = '';
                this.transferStatusMessage = '';
                this.canResendTicket = false;
                this.immediateTransferCompleted = false;
                this.clearSettlementFields();
                this.copyInvoiceMessage = '';
            },
            updateTypeaheadString: function() {
                // Placeholder for typeahead search update
            },
            updateTargetEvent: function(event) {
                this.targetEvent = event;
                this.resetTransferOutcome();
            },
            normaliseSettlement: function(data) {
                const nested = data.settlement && typeof data.settlement === 'object' ? data.settlement : null;
                const src = nested || {};
                return {
                    reference: src.reference || src.settlementReference || data.settlementReference || '',
                    state: src.state || data.settlementState || '',
                    invoiceUrl: src.invoiceUrl || src.invoice_url || data.invoiceUrl || data.invoice_url || '',
                    invoiceExpiresAt: src.invoiceExpiresAt || src.invoice_expires_at || data.invoiceExpiresAt || data.invoice_expires_at || '',
                };
            },
            applySettlementFromPayload: function(data) {
                const n = this.normaliseSettlement(data);
                if (n.reference) {
                    this.settlementReference = n.reference;
                }
                if (n.state) {
                    this.settlementState = n.state;
                }
                if (n.invoiceUrl) {
                    this.settlementInvoiceUrl = n.invoiceUrl;
                }
                if (n.invoiceExpiresAt) {
                    this.invoiceExpiresAt = n.invoiceExpiresAt;
                }
            },
            clearSettlementFields: function() {
                this.settlementReference = '';
                this.settlementState = '';
                this.settlementInvoiceUrl = '';
                this.invoiceExpiresAt = '';
            },
            handleTransferResponsePayload: function(data) {
                const vm = this;
                vm.asyncFormError = '';
                vm.transferStatusMessage = '';

                if (!data || typeof data !== 'object') {
                    vm.asyncFormError = 'Unexpected response from server.';
                    return;
                }

                if (data.status === 'error') {
                    vm.canResendTicket = false;
                    vm.immediateTransferCompleted = false;
                    if (data.settlement && typeof data.settlement === 'object') {
                        vm.applySettlementFromPayload({ settlement: data.settlement });
                    } else if (!data.settlementReference && !data.settlementState) {
                        vm.clearSettlementFields();
                    }
                    vm.asyncFormError = data.reason || 'Transfer could not be completed.';
                    return;
                }

                if (data.status === 'pending') {
                    vm.immediateTransferCompleted = false;
                    vm.canResendTicket = false;
                    vm.applySettlementFromPayload(data);
                    vm.transferStatusMessage = 'Transfer request recorded; settlement is in progress.';
                    vm.$emit('activity-recorded');
                    return;
                }

                if (data.status === 'success') {
                    const hasSettlementBlock = (data.settlement && typeof data.settlement === 'object')
                        || data.settlementReference
                        || data.settlementState;
                    if (!hasSettlementBlock) {
                        vm.clearSettlementFields();
                    }
                    vm.applySettlementFromPayload(data);

                    if (vm.settlementState === 'settled') {
                        vm.settlementInvoiceUrl = '';
                        vm.invoiceExpiresAt = '';
                    }

                    if (vm.settlementState === 'failed' || vm.settlementState === 'expired') {
                        vm.canResendTicket = false;
                        vm.immediateTransferCompleted = false;
                        if (data.reason) {
                            vm.asyncFormError = data.reason;
                        }
                        return;
                    }

                    if (vm.settlementState === 'settled') {
                        vm.immediateTransferCompleted = false;
                        vm.canResendTicket = true;
                        vm.transferStatusMessage = 'Transfer completed successfully.';
                        vm.$emit('activity-recorded');
                        return;
                    }

                    vm.immediateTransferCompleted = true;
                    vm.canResendTicket = true;
                    vm.transferStatusMessage = 'Transfer completed successfully.';
                    vm.$emit('activity-recorded');
                    return;
                }

                vm.asyncFormError = 'Unexpected response from server.';
            },
            transferTicket: function(e) {
                e.preventDefault();
                const vm = this;
                if (vm.isDisabled) {
                    return;
                }
                vm.applyAuthHeader();
                vm.asyncFormError = '';
                vm.transferStatusMessage = '';
                vm.copyInvoiceMessage = '';
                vm.canResendTicket = false;
                vm.immediateTransferCompleted = false;

                const url = vm.requestUrl('/einstein/ticket/transfer');
                const postData = {
                    ticketCode: vm.ticket.code,
                    eventId: vm.targetEvent.id,
                };

                vm.axiosInstance.post(url, postData)
                    .then(function(response) {
                        vm.handleTransferResponsePayload(response.data);
                    })
                    .catch(function(error) {
                        vm.asyncFormError = errorModel.apiError(error, vm.$router) || 'Request failed.';
                    });
            },
            buildSettlementStatusUrl: function() {
                const params = new URLSearchParams({
                    ticketCode: this.ticket.code,
                    settlementReference: this.settlementReference,
                });
                return this.requestUrl(SETTLEMENT_STATUS_PATH + '?' + params.toString());
            },
            refreshSettlementStatus: function() {
                const vm = this;
                if (!vm.settlementReference) {
                    return;
                }
                vm.applyAuthHeader();
                vm.refreshInFlight = true;
                vm.asyncFormError = '';
                vm.axiosInstance.get(vm.buildSettlementStatusUrl())
                    .then(function(response) {
                        vm.handleTransferResponsePayload(response.data);
                    })
                    .catch(function(error) {
                        vm.asyncFormError = errorModel.apiError(error, vm.$router) || 'Request failed.';
                    })
                    .finally(function() {
                        vm.refreshInFlight = false;
                    });
            },
            resendTicketEmail: function() {
                const vm = this;
                const ticketCode = String(vm.ticket.code);

                if (vm.isResendDisabled) {
                    return;
                }

                vm.resendingTicket = true;
                vm.applyAuthHeader();

                vm.axiosInstance
                    .post(vm.requestUrl(TICKET_RESEND_PATH), buildTicketResendPayload(ticketCode))
                    .then(function(response) {
                        vm.resendingTicket = false;
                        if (isTicketResendQueued(response.data)) {
                            vm.ticketResent = true;
                            toastModel.show('Ticket resent');
                            return;
                        }
                        if (response.data && response.data.status === 'error') {
                            vm.asyncFormError = response.data.reason || 'Failed to resend ticket';
                        }
                    })
                    .catch(function(error) {
                        vm.resendingTicket = false;
                        const responseError =
                            error.response && error.response.data && error.response.data.error;
                        vm.asyncFormError =
                            errorModel.apiError(error, vm.$router) ||
                            responseError ||
                            'Failed to resend ticket';
                    });
            },
            copyInvoiceLink: function() {
                const vm = this;
                const url = vm.settlementInvoiceUrl;
                if (!url || !navigator.clipboard || !navigator.clipboard.writeText) {
                    vm.copyInvoiceMessage = '';
                    return;
                }
                navigator.clipboard.writeText(url)
                    .then(function() {
                        vm.copyInvoiceMessage = 'Link copied';
                        window.setTimeout(function() {
                            vm.copyInvoiceMessage = '';
                        }, 2000);
                    })
                    .catch(function() {
                        vm.copyInvoiceMessage = '';
                    });
            },
        },
        data: function () {
            return {
                baseUrl: process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL : '/',
                axiosInstance: axios.create(),
                targetEvent: false,
                targetEventSearch: '',
                asyncFormError: '',
                transferStatusMessage: '',
                canResendTicket: false,
                ticketResent: false,
                resendingTicket: false,
                immediateTransferCompleted: false,
                settlementReference: '',
                settlementState: '',
                settlementInvoiceUrl: '',
                invoiceExpiresAt: '',
                refreshInFlight: false,
                copyInvoiceMessage: '',
            }
        }
    }
</script>
